import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CaretDownIcon } from '@phosphor-icons/react'
import { AnimatePresence } from 'motion/react'
import { motion } from 'motion/react'
import { useTheme } from 'styled-components'

import { Checkbox, Chip, CountryFlag, FormatPrice, Text } from '@reveni-io/reveni-ui'

import CompletedInfoChip from './CompletedInfoChip/CompletedInfoChip'
import ExportPrice from './ExportPrice/ExportPrice'
import ExportPriceChip from './ExportPriceChip/ExportPriceChip'
import IncompleteIdentifiersChip from './IncompleteIdentifiersChip/IncompleteIdentifiersChip'
import ItemIdentifiers from './ItemIdentifiers/ItemIdentifiers'
import MissingInfoChip from './MissingInfoChip/MissingInfoChip'
import { identifiersStatuses } from './constants'
import {
  getIncompleteIdentifiersCount,
  getProductIdentifiersStatus,
  haveAllItemsCountry,
  haveAllItemsExportPrice,
  haveAllItemsHSCode,
  haveAllItemsUnitCost,
  haveAllItemsWeight,
} from './helpers'
import * as Styles from './styles'

const ProductRow = ({ product, position, onClickInItem, currency, selected, onCheckProduct }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [showItems, setShowItems] = useState(false)

  const identifiersStatus = getProductIdentifiersStatus(product)

  const onClickInProduct = () => {
    setShowItems(!showItems)
  }

  return (
    <>
      <Styles.SpacerRow />
      <Styles.ProductRow onClick={onClickInProduct} selected={selected} expanded={showItems}>
        <Styles.CheckboxCell onClick={e => e.stopPropagation()}>
          <Checkbox checked={selected} onChange={e => onCheckProduct(e, product.id)} />
        </Styles.CheckboxCell>
        <td>
          <Text type="bodyMRegular" color={theme.colors.text.subtle}>
            {position}
          </Text>
        </td>
        <Styles.ProductImageCell>
          <Styles.ProductImage src={product.image_url || '/images/placeholderImg.png'} />
        </Styles.ProductImageCell>
        <Styles.ProductCell>
          <Text type="bodyMBold">{product.title}</Text>
          {product?.category && <Chip label={product?.category?.name} />}
          <Text type="bodyMRegular">
            {product?.inventory_items?.length === 1
              ? t('1 variante')
              : t('{{number}} variantes', { number: product.inventory_items.length })}
          </Text>
        </Styles.ProductCell>
        <td>{haveAllItemsCountry(product) ? <CompletedInfoChip /> : <MissingInfoChip />}</td>
        <td>{haveAllItemsHSCode(product) ? <CompletedInfoChip /> : <MissingInfoChip />}</td>
        <td>
          {identifiersStatus === identifiersStatuses.completed && <CompletedInfoChip />}
          {identifiersStatus === identifiersStatuses.missing && <MissingInfoChip />}
          {identifiersStatus === identifiersStatuses.partial && (
            <IncompleteIdentifiersChip
              incompleteCount={getIncompleteIdentifiersCount(product)}
              totalCount={product.inventory_items.length}
            />
          )}
        </td>
        <td>{haveAllItemsUnitCost(product) ? <CompletedInfoChip /> : <MissingInfoChip />}</td>
        <td>{haveAllItemsWeight(product) ? <CompletedInfoChip /> : <MissingInfoChip />}</td>
        <td>{haveAllItemsExportPrice(product) ? <ExportPriceChip product={product} /> : '-'}</td>
        <motion.td animate={{ rotate: showItems ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <CaretDownIcon size={24} />
        </motion.td>
      </Styles.ProductRow>
      <AnimatePresence>
        {showItems &&
          product.inventory_items.map((item, index) => (
            <Styles.VariantRow
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{
                duration: 0.3,
              }}
              key={item.id}
              selected={selected}
              isFirst={index === 0}
              isLast={index === product.inventory_items.length - 1}
              onClick={() => onClickInItem(item, product)}
            >
              <td></td>
              <td></td>
              <td style={{ position: 'relative' }}>
                <Styles.VerticalUnion isFirstItem={index === 0} />
                <Styles.HorizontalUnion />
              </td>
              <Styles.ItemCell>
                <Text type="bodyMRegular" color={theme.colors.text.subtle}>
                  {index + 1}
                </Text>
                <Styles.ItemImage src={item.image_url || product.image_url || '/images/placeholderImg.png'} />
                <Styles.ItemInfoContainer>
                  <Text type="bodyMBold">{item.title}</Text>
                  <Text type="allCapsM">{item.sku}</Text>
                </Styles.ItemInfoContainer>
              </Styles.ItemCell>
              <td>
                <Styles.ItemCountryInfo>
                  <CountryFlag code={item?.country?.code} />
                  <Text type="bodyMRegular">{item?.country?.name || <MissingInfoChip />}</Text>
                </Styles.ItemCountryInfo>
              </td>
              <td>
                <Text type="bodyMRegular">{item.hs_code || <MissingInfoChip />}</Text>
              </td>
              <td>
                <ItemIdentifiers item={item} />
              </td>
              <td>
                <Text type="bodyMRegular">
                  {Number(item.unit_cost) ? (
                    <FormatPrice currency={currency}>{item.unit_cost}</FormatPrice>
                  ) : (
                    <MissingInfoChip />
                  )}
                </Text>
              </td>
              <td>
                <Text type="bodyMRegular">
                  {item.weight ? `${item.weight} ${item.weight_unit}` : <MissingInfoChip />}
                </Text>
              </td>
              <td>
                <Text type="bodyMRegular">
                  <ExportPrice item={item} product={product} />
                </Text>
              </td>
              <td></td>
            </Styles.VariantRow>
          ))}
      </AnimatePresence>
    </>
  )
}

export default ProductRow
